import { gql } from "graphql-tag";
import { useMutation } from "@apollo/client";
import { navigate } from "utils/navigation";
import { getReturnToUrl } from "utils/login";
import { Options } from "components/haven-design/snackbar/use-snackbar";
import { loginWithHavenToken } from "utils/general-utils";

const SEND_RESET_PASSWORD_EMAIL = gql`
  mutation SendResetPasswordEmail($email: String!, $returnUrl: String) {
    sendResetPasswordEmail(email: $email, returnUrl: $returnUrl)
  } 
`;

const SEND_ENROLLMENT_EMAIL = gql`
  mutation SendEnrollmentEmail($userId: ID!) {
    sendEnrollmentEmail(userId: $userId)
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $password: String!) {
    resetPassword(token: $token, password: $password) {
      success
      token
      returnUrl
    }
  }
`;

export const useChooseNewPassword = () => {
  const [resetPassword] = useMutation(RESET_PASSWORD);

  return (token: string, newPassword: string): Promise<string> =>
    new Promise((resolve, reject) => {
      resetPassword({ variables: { token, password: newPassword } })
        .then(({ data }) => {
          if (data.resetPassword.success) {
            loginWithHavenToken(data.resetPassword.token);
            resolve(data.resetPassword.returnUrl || "");
          } else {
            reject(new Error("Failed to reset password"));
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
};

export const useSetNewUserPassword = () => {
  const [resetPassword] = useMutation(RESET_PASSWORD);

  return (token: string, newPassword: string): Promise<string> =>
    new Promise((resolve, reject) => {
      resetPassword({ variables: { token, password: newPassword } })
        .then(({ data }) => {
          if (data.resetPassword.success) {
            loginWithHavenToken(data.resetPassword.token);
            resolve(data.resetPassword.returnUrl || "");
          } else {
            reject(new Error("Failed to set password"));
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
};

export const useRequestResetPasswordEmail = () => {
  const [sendResetPasswordEmail] = useMutation(SEND_RESET_PASSWORD_EMAIL);

  return ({ email }: { email: string }): Promise<void> =>
    new Promise((resolve) => {
      sendResetPasswordEmail({ variables: { email, returnUrl: getReturnToUrl() } })
        .then(() => {
          navigate(`/check-email?email=${email}`);
          resolve();
        })
        .catch(() => {
          navigate(`/no-account?email=${email}`);
          resolve();
        });
    });
};

export const useResetPasswordEmail = () => {
  const [sendResetPasswordEmail] = useMutation(SEND_RESET_PASSWORD_EMAIL);

  return (email: string, openSnackbar: (options: Options) => void): Promise<void> =>
    sendResetPasswordEmail({ variables: { email } })
      .then(() => {
        openSnackbar({ message: "Reset Password Email Sent", status: "success" });
      })
      .catch(() => {
        openSnackbar({ message: "Reset Password Email Failed to Send", status: "error" });
      });
};

type SendEnrollmentEmailOptions = {
  // When false, skip navigating to team settings after the email is sent (default: redirect).
  redirect?: boolean;
};

export const useSendEnrollmentEmail = () => {
  const [sendEnrollmentEmailMutation] = useMutation(SEND_ENROLLMENT_EMAIL);

  return (
    userId: string,
    openSnackbar: (options: Options) => void,
    options?: SendEnrollmentEmailOptions,
  ): Promise<string | void> =>
    new Promise((resolve, reject) => {
      sendEnrollmentEmailMutation({ variables: { userId } })
        .then(() => {
          openSnackbar({ message: "Success", status: "success" });
          if (options?.redirect !== false) {
            navigate("/dash/manager/settings/team");
          }
          resolve();
        })
        .catch(() => {
          openSnackbar({ message: "Error creating account", status: "error" });
          reject(new Error("Error creating account"));
        });
    });
};
